import React, { useState, useEffect } from 'react';
import { jobsAPI } from '../services/api';
import './Jobs.css';

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchJobs();
      }, 5000); // Refresh every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await jobsAPI.getAllJobs();
      setJobs(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobDetails = async (jobId) => {
    try {
      const response = await jobsAPI.getJob(jobId);
      setSelectedJob(response.data.data);
    } catch (err) {
      console.error('Failed to fetch job details:', err);
    }
  };

  const handleResumeJob = async (jobId) => {
    try {
      await jobsAPI.resumeJob(jobId);
      alert('Job resume initiated. Refreshing...');
      setTimeout(() => fetchJobs(), 1000);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to resume job');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#28a745';
      case 'processing':
        return '#007bff';
      case 'failed':
        return '#dc3545';
      case 'paused':
        return '#ffc107';
      default:
        return '#6c757d';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatTimeRemaining = (estimatedTimeString) => {
    if (!estimatedTimeString) return 'Calculating...';
    
    const now = new Date();
    const estimated = new Date(estimatedTimeString);
    const diffMs = estimated - now;
    
    if (diffMs <= 0) return 'Almost done...';
    
    const diffSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);
    const seconds = diffSeconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  return (
    <div className="jobs">
      <div className="jobs-header">
        <h1 className="page-title">Email Jobs</h1>
        <div className="jobs-controls">
          <button 
            className="btn btn-secondary" 
            onClick={fetchJobs}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <label style={{ marginLeft: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
        </div>
      </div>

      {jobs.length === 0 ? (
        <div className="card">
          <p>No jobs found. Upload a CSV file to create a job.</p>
        </div>
      ) : (
        <div className="jobs-list">
          {jobs.map((job) => (
            <div key={job.id} className="job-card">
              <div className="job-header">
                <div className="job-info">
                  <h3>Job: {job.id}</h3>
                  <span 
                    className="job-status"
                    style={{ backgroundColor: getStatusColor(job.status) }}
                  >
                    {job.status.toUpperCase()}
                  </span>
                </div>
                <div className="job-actions">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => fetchJobDetails(job.id)}
                  >
                    View Details
                  </button>
                  {(job.status === 'failed' || job.status === 'paused') && (
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleResumeJob(job.id)}
                      style={{ marginLeft: '0.5rem' }}
                    >
                      Resume
                    </button>
                  )}
                </div>
              </div>

              <div className="job-details">
                <div className="job-detail-item">
                  <strong>Template ID:</strong> {job.templateId}
                </div>
                <div className="job-detail-item">
                  <strong>Created:</strong> {formatDate(job.createdAt)}
                </div>
                <div className="job-detail-item">
                  <strong>Updated:</strong> {formatDate(job.updatedAt)}
                </div>
                {job.totalRows > 0 && (
                  <>
                    <div className="job-detail-item">
                      <strong>Progress:</strong> {job.checkpoint.processedCount} / {job.totalRows} processed
                      {job.checkpoint.failedCount > 0 && (
                        <span style={{ color: '#dc3545', marginLeft: '1rem' }}>
                          ({job.checkpoint.failedCount} failed)
                        </span>
                      )}
                    </div>
                    {job.status === 'processing' && job.metrics && (
                      <>
                        <div className="job-detail-item">
                          <strong>Rate:</strong> {job.metrics.emailsPerSecond || 0} emails/second
                        </div>
                        {job.metrics.estimatedTimeRemaining && (
                          <div className="job-detail-item">
                            <strong>Est. Time Remaining:</strong> {formatTimeRemaining(job.metrics.estimatedTimeRemaining)}
                          </div>
                        )}
                        {job.metrics.estimatedCompletionTime && (
                          <div className="job-detail-item">
                            <strong>Est. Completion:</strong> {formatDate(job.metrics.estimatedCompletionTime)}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
                {job.checkpoint.lastProcessedIndex >= 0 && (
                  <div className="job-detail-item">
                    <strong>Last Processed Index:</strong> {job.checkpoint.lastProcessedIndex}
                  </div>
                )}
              </div>

              {selectedJob && selectedJob.id === job.id && (
                <div className="job-expanded-details">
                  <h4>Detailed Information</h4>
                  {selectedJob.metrics && (
                    <div className="job-detail-section">
                      <strong>Performance Metrics:</strong>
                      <div style={{ 
                        background: '#f5f5f5', 
                        padding: '1rem', 
                        borderRadius: '4px',
                        marginTop: '0.5rem'
                      }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong>Processing Rate:</strong> {selectedJob.metrics.emailsPerSecond || 0} emails/second
                        </div>
                        {selectedJob.metrics.estimatedTimeRemaining && (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>Estimated Time Remaining:</strong> {formatTimeRemaining(selectedJob.metrics.estimatedCompletionTime)}
                          </div>
                        )}
                        {selectedJob.metrics.estimatedCompletionTime && (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>Estimated Completion:</strong> {formatDate(selectedJob.metrics.estimatedCompletionTime)}
                          </div>
                        )}
                        {selectedJob.metrics.startTime && (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>Started:</strong> {formatDate(selectedJob.metrics.startTime)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {selectedJob.s3Key && (
                    <div className="job-detail-section">
                      <strong>S3 Key:</strong> {selectedJob.s3Key}
                    </div>
                  )}
                  <div className="job-detail-section">
                    <strong>Checkpoint:</strong>
                    <pre style={{ 
                      background: '#f5f5f5', 
                      padding: '1rem', 
                      borderRadius: '4px',
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}>
                      {JSON.stringify(selectedJob.checkpoint, null, 2)}
                    </pre>
                  </div>
                  {selectedJob.results && selectedJob.results.length > 0 && (
                    <div className="job-detail-section">
                      <strong>Results ({selectedJob.results.length}):</strong>
                      <div style={{ 
                        maxHeight: '300px', 
                        overflow: 'auto',
                        marginTop: '0.5rem'
                      }}>
                        {selectedJob.results.slice(-20).map((result, idx) => (
                          <div 
                            key={idx} 
                            style={{ 
                              padding: '0.5rem',
                              marginBottom: '0.5rem',
                              background: result.success ? '#d4edda' : '#f8d7da',
                              borderRadius: '4px',
                              fontSize: '0.9rem'
                            }}
                          >
                            <strong>Row {result.index}:</strong> {result.email} - 
                            {result.success ? (
                              <span style={{ color: '#28a745' }}> ✓ Success</span>
                            ) : (
                              <span style={{ color: '#dc3545' }}> ✗ Failed: {result.error}</span>
                            )}
                          </div>
                        ))}
                        {selectedJob.results.length > 20 && (
                          <p style={{ color: '#666', fontSize: '0.9rem' }}>
                            Showing last 20 results. Total: {selectedJob.results.length}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  {selectedJob.error && (
                    <div className="job-detail-section">
                      <strong style={{ color: '#dc3545' }}>Error:</strong> {selectedJob.error}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Jobs;

